import type { PartyBucket } from './raceBuckets';

/** Hover/open-card brightness shared by both maps' style sheets. */
export const HOVER_FILTER = 'filter: brightness(0.85);';

/**
 * The gray of a region with no paintable race data — the maps' default path
 * fill, the bucket filter's non-matching fill, and the House bar's
 * missing-market segment, so "no market here" reads identically everywhere
 * in every theme.
 */
export const RACELESS_REGION_FILL = 'var(--container-x30)';

/**
 * Whether the `?bucket=` filter (set by the chamber-control card) grays a
 * region out: a filter is active, the map takes one (`regionBuckets` set),
 * and the region isn't rated in that bucket.
 */
export function isRegionFilteredOut(
    regionBuckets: Record<string, PartyBucket> | undefined,
    activeBucket: PartyBucket | null,
    regionId: string,
): boolean {
    return regionBuckets !== undefined && activeBucket !== null && regionBuckets[regionId] !== activeBucket;
}

/**
 * Scoped CSS rules painting each region's fill onto the injected (non-React)
 * map paths, shared by the district and state maps. `!important` because some
 * injected paths carry inline `style` fills that beat non-important rules.
 *
 * With an active `?bucket=` filter (set by the chamber-control card), regions
 * outside the bucket gray out and go inert; `regionBuckets` omitted disables
 * the filter entirely (maps without a card).
 */
export function buildRegionFillRules(
    scopeClass: string,
    fills: Record<string, string>,
    regionBuckets: Record<string, PartyBucket> | undefined,
    activeBucket: PartyBucket | null,
): string {
    const filterBucket = regionBuckets !== undefined && activeBucket !== null ? activeBucket : null;
    const rules = Object.entries(fills).map(([regionId, fill]) => {
        if (filterBucket === null) {
            return `.${scopeClass} [data-map-layer] #${regionId}{fill:${fill} !important}`;
        }
        // Filtered view: matching regions keep their fill and re-enable
        // pointer events over the blanket rule below (id beats element
        // specificity); everything else reads as a race-less gray.
        return isRegionFilteredOut(regionBuckets, filterBucket, regionId)
            ? `.${scopeClass} [data-map-layer] #${regionId}{fill:${RACELESS_REGION_FILL} !important}`
            : `.${scopeClass} [data-map-layer] #${regionId}{fill:${fill} !important;pointer-events:auto}`;
    });
    if (filterBucket !== null) {
        // Deadens hover cards, hover brightness, prefetch, and click-to-zoom
        // on every non-matching path — including regions with no fill entry,
        // which stay hoverable otherwise.
        rules.unshift(`.${scopeClass} [data-map-layer] path{pointer-events:none}`);
    }
    return rules.join('\n');
}
